import type { LevelDefinition } from "@algoquest/schemas";
import { createCampaignLevel } from "./levelFactory";

/**
 * Phase 15 -- World 1: "Searching Fundamentals" (Master Spec Section 5), campaign
 * levels 1-10. Teaches: target, dataset, item, position, comparison, found/not found, sequential inspection concept.
 * Algorithm(s) used in this world: linear-search.
 */
export const world01SearchingFundamentalsLevels: LevelDefinition[] = [
  createCampaignLevel({
    id: "campaign-001",
    world: 1,
    levelInWorld: 1,
    globalLevelNumber: 1,
    algorithm: "linear-search",
    values: [14, 9, 22, 31, 7],
    target: 14,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Learn what a target value is and how it compares to each item in a dataset.",
      "See the very first item checked and immediately match -- that still counts as a real comparison.",
    ],
    notes: "Target sits at index 0 -- the simplest possible case: one comparison, one match.",
  }),
  createCampaignLevel({
    id: "campaign-002",
    world: 1,
    levelInWorld: 2,
    globalLevelNumber: 2,
    algorithm: "linear-search",
    values: [19, 9, 31, 22, 7],
    target: 9,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Practice checking items one position at a time, in order.",
      "See position (index) as simply how far into the dataset an item sits.",
    ],
    notes: "Target is the second item -- introduces checking more than one position before a match.",
  }),
  createCampaignLevel({
    id: "campaign-003",
    world: 1,
    levelInWorld: 3,
    globalLevelNumber: 3,
    algorithm: "linear-search",
    values: [19, 9, 31, 22, 7, 14],
    target: 22,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "See several non-matching comparisons happen before the match.",
      "Reinforce that every item is inspected in the same simple way, regardless of position.",
    ],
    notes: "Target sits roughly in the middle of a 6-item dataset.",
  }),
  createCampaignLevel({
    id: "campaign-004",
    world: 1,
    levelInWorld: 4,
    globalLevelNumber: 4,
    algorithm: "linear-search",
    values: [19, 9, 31, 22, 7, 14],
    target: 7,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Practice sequential inspection continuing well past the middle of the dataset.",
      "Notice how many comparisons a later position costs.",
    ],
    notes: "Target is near the end -- most items are checked before it is found.",
  }),
  createCampaignLevel({
    id: "campaign-005",
    world: 1,
    levelInWorld: 5,
    globalLevelNumber: 5,
    algorithm: "linear-search",
    values: [19, 9, 31, 22, 7, 14],
    target: 14,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "See the found outcome fire only after checking every earlier item.",
      "Understand position (index) as the exact count of items inspected before this one.",
    ],
    notes: "Target is the very last item -- the most comparisons a found result can take here.",
  }),
  createCampaignLevel({
    id: "campaign-006",
    world: 1,
    levelInWorld: 6,
    globalLevelNumber: 6,
    algorithm: "linear-search",
    values: [19, 9, 31, 22, 7, 14],
    target: 40,
    targetPresent: false,
    isSorted: false,
    learningObjectives: [
      "See the not found outcome after every item has been inspected and none matched.",
      "Understand that concluding absence requires checking the whole dataset.",
    ],
    notes: "40 does not appear anywhere in this dataset -- every item is checked, none match.",
  }),
  createCampaignLevel({
    id: "campaign-007",
    world: 1,
    levelInWorld: 7,
    globalLevelNumber: 7,
    algorithm: "linear-search",
    values: [21, 13, 8, 33, 26, 17],
    target: 26,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Practice careful comparison when nearby values are close to the target.",
      "Reinforce that a comparison checks exact equality, not closeness.",
    ],
    notes: "17 and 33 are close to the target 26 but not equal -- only an exact match counts as found.",
  }),
  createCampaignLevel({
    id: "campaign-008",
    world: 1,
    levelInWorld: 8,
    globalLevelNumber: 8,
    algorithm: "linear-search",
    values: [5, 44, 12, 27, 9, 38, 16],
    target: 5,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Apply sequential inspection to a slightly larger, 7-item dataset.",
      "See that a match at the very start is just as valid on a larger dataset.",
    ],
    notes: "First 7-item dataset in the fundamentals world -- target found immediately.",
  }),
  createCampaignLevel({
    id: "campaign-009",
    world: 1,
    levelInWorld: 9,
    globalLevelNumber: 9,
    algorithm: "linear-search",
    values: [5, 44, 12, 27, 9, 38, 16],
    target: 16,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "See a found result require checking every earlier item on a larger dataset.",
      "Build intuition that more items can mean more comparisons before success.",
    ],
    notes: "Target is the last of 7 items -- contrast with level 8's immediate match.",
  }),
  createCampaignLevel({
    id: "campaign-010",
    world: 1,
    levelInWorld: 10,
    globalLevelNumber: 10,
    algorithm: "linear-search",
    values: [5, 44, 12, 27, 9, 38, 16],
    target: 50,
    targetPresent: false,
    isSorted: false,
    learningObjectives: [
      "Complete a full not found search on the fundamentals world's largest dataset.",
      "Consolidate the whole fundamentals loop: target, item, position, comparison, found vs not found.",
    ],
    notes: "Capstone of World 1 -- 50 is absent from all 7 items; every item is checked before concluding not found.",
  }),
];
